<template>
  <div class="mt-6 leading-normal text-sm">
    <div class="flex justify-between mb-6" v-if="form">
      <h2>{{ form.name }}</h2>
      <div>
        <button @click="back" class="bg-white hover:bg-grey text-primary py-2 px-6 text-sm border border-grey-dark rounded shadow whitespace-no-wrap ml-3">
          <i class="fas fa-arrow-left"></i> Back
        </button>

        <button @click="view" class="bg-white hover:bg-grey text-primary py-2 px-6 text-sm border border-grey-dark rounded shadow whitespace-no-wrap ml-3">
          <i class="fas fa-eye"></i> View
        </button>

        <button v-if="form.approved_at && !form.deleted_at" @click="deleteModalOpen = true" class="bg-white hover:bg-grey text-primary py-2 px-6 text-sm border border-grey-dark rounded shadow whitespace-no-wrap ml-3" :disabled="deleting">
          <loader fill-color="white" v-if="deleting" />
          <span v-else><i class="fas fa-trash-alt"></i> Deactivate</span>
        </button>

        <button v-if="!form.deleted_at" @click="save" class="bg-tertiary text-white py-2 px-6 text-sm border border-tertiary hover:border-transparent rounded shadow whitespace-no-wrap ml-3" :disabled="saving">
          <loader fill-color="white" v-if="saving" />
          <span v-else><i class="fas fa-cloud-upload-alt"></i> Save Changes</span>
        </button>
      </div>
    </div>

    <loader v-if="loading" />
    <div v-else>
      <div v-if="form.deleted_at" class="bg-red-lightest border border-red-dark px-6 py-3 rounded-lg mb-6">
        <h3 class="text-sm">Edition Expired</h3>
        <p>This edition of your campaign has expired and is no longer active.</p>
      </div>
      <div v-else-if="!form.approved_at" class="bg-yellow-lightest border border-yellow-dark px-6 py-3 rounded-lg mb-6">
        <h3 class="text-sm">Pending Approval</h3>
        <p>
          This edition of your e-zine campaign is pending approval. Please review the contents and click approve when you are ready. You can cancel this edition of your campaign by selecting reject.
          <span v-if="form.campaign.auto_approval_enabled">This edition will auto-approve on {{ form.delivery_date | date }} if no response is received.</span>
        </p>
        <div class="flex justify-end mt-6">
          <button class="bg-transparent text-white py-2 px-6 text-sm bg-red-dark hover:bg-red rounded" @click="rejectModalOpen = true">
            <i class="fas fa-times-circle"></i> Reject
          </button>

          <button class="bg-transparent text-white py-2 px-6 text-sm bg-green-dark hover:bg-green rounded shadow ml-3" @click="approveModalOpen = true">
            <i class="fas fa-check-circle"></i> Approve Edition
          </button>
        </div>
      </div>
      <div v-else-if="!form.sent_at" class="bg-yellow-lightest border border-yellow-dark px-6 py-3 rounded-lg mb-6">
        <h3 class="text-sm">Approved</h3>
        <p>This edition of your e-zine campaign has been approved.</p>
      </div>
      <div v-else class="bg-green-lightest border border-green-dark px-6 py-3 rounded-lg mb-6">
        <h3 class="text-sm">Active</h3>
        <p>This is the current, active edition of your campaign.</p>
      </div>

      <div class="ml-3">
        <button @click="tab = 'properties'" class="rounded-t-lg py-3 px-6 mr-1" :class="tab === 'properties' ? 'bg-white' : 'bg-grey-light hover:bg-grey'">
          Included Properties
        </button>
        <button @click="tab = 'pages'" class="rounded-t-lg py-3 px-6 mr-1" :class="tab === 'pages' ? 'bg-white' : 'bg-grey-light hover:bg-grey'">
          Pages
        </button>
      </div>
      <!-- INCLUDED PROPERTIES -->
      <panel v-if="tab === 'properties'" class="pt-6">
        <ezine-properties-tab v-model="form" ref="ezinePropertyTab" />
      </panel>

      <!-- PAGES -->
      <panel v-else-if="tab === 'pages'" class="pt-6">
        <ezine-page-layout-tab type="edition" v-model="form" :errorBag="errorBag" :page-groups="pageGroups" :editable="form.deleted_at === null"  :offices="$refs.ezinePropertyTab.offices" />
      </panel>
    </div>

    <deletion-modal
      v-if="approveModalOpen"
      @confirmed="approveEdition"
      @leave="approveModalOpen = false"
      @dismissed="approveModalOpen = false"
      confirm-label="Yes, approve it">
      <p class="text-center">Are you sure you want to approve this edition? The final copy will be delivered to your recipients immediately.</p>
    </deletion-modal>

    <deletion-modal
      v-if="rejectModalOpen"
      @confirmed="deleteEdition"
      @leave="rejectModalOpen = false"
      @dismissed="rejectModalOpen = false"
      confirm-label="Yes, reject it">
      <p class="text-center">Are you sure you want to reject this edition? Another edition will be generated at the next due date.</p>
    </deletion-modal>

    <deletion-modal
      v-if="deleteModalOpen"
      @confirmed="deleteEdition"
      @leave="deleteModalOpen = false"
      @dismissed="deleteModalOpen = false"
      confirm-label="Yes, deactivate it">
      <p class="text-center">Are you sure you want to deactivate this e-zine?</p>
    </deletion-modal>
  </div>
</template>

<script>
import { v2Api } from '@/http'
import ErrorBag from '@/ErrorBag'
import { mapActions, mapState } from 'vuex'
import { map } from 'lodash'
import randomstring from 'randomstring'
import EzinePropertiesTab from './tabs/EzinePropertiesTab.vue'
import EzinePageLayoutTab from './tabs/EzinePageLayoutTab.vue'
import DeletionModal from '@/components/Modals/DeletionModal'

export default {
  components: { EzinePropertiesTab, EzinePageLayoutTab, DeletionModal },
  props: {
    editionId: {
      required: true
    }
  },
  data () {
    return {
      loading: true,
      form: null,
      tab: 'properties',
      errorBag: new ErrorBag(),
      pageGroups: null,
      saving: false,
      rejectModalOpen: false,
      approveModalOpen: false,
      deleteModalOpen: false,
      approving: false,
      deleting: false
    }
  },
  computed: {
    ...mapState(['selectedOffice'])
  },
  created () {
    this.init()
  },
  methods: {
    ...mapActions(['displayToastNotification']),
    init () {
      Promise.all([
        this.fetchEdition()
      ]).then(() => {
        this.getPageGroups().then(() => {
          this.loading = false
        })
      })
    },
    fetchEdition () {
      return v2Api.get(`/ezine-editions/${this.editionId}`).then((response) => {
        response.data.pages = map(response.data.pages, (page) => {
          page.ref = randomstring.generate()
          return page
        })
        this.form = response.data
      })
    },
    getPageGroups () {
      return v2Api.get('ezine-page-groups', {
        params: {
          client_id: this.selectedOffice.client_id,
          ezine_campaign_id: this.form.ezine_campaign_id,
          with_network_groups: 1
        }
      }).then((response) => {
        const orderMap = ['App\\Models\\EzineCampaign', 'App\\Models\\Client', 'App\\Models\\Network']
        this.pageGroups = response.data.sort(function (a, b) {
          return orderMap.indexOf(a.assignable_type) > orderMap.indexOf(b.assignable_type)
        })
      })
    },
    view () {
      window.open(this.form.url, '_blank')
    },
    back () {
      this.$emit('change-edition', null)
    },
    save () {
      this.saving = true
      this.errorBag.clear()
      let form = {
        front_cover_property_id: this.form.front_cover_property_id,
        featured_properties: this.form.featured_properties,
        properties_list: this.form.properties_list,
        pages: this.form.pages.map((page) => page.id)
      }

      v2Api.patch(`ezine-editions/${this.form.id}`, form).then((response) => {
        this.saving = false

        this.displayToastNotification({
          type: 'success',
          icon: 'fa-check-circle',
          title: 'Saved',
          info: 'Changes to this edition have been saved.'
        })
      }, (errors) => {
        this.errorBag.store(errors.response.data)
        this.saving = false

        this.displayToastNotification({
          type: 'danger',
          icon: 'fa-exclamation-triangle',
          title: 'Error',
          info: errors.response.data.message
        })
      })
    },
    approveEdition () {
      this.approving = true
      v2Api.post(`ezine-editions/${this.form.id}/approve`).then((response) => {
        this.form.approved_at = response.data.approved_at
        this.approving = false
        this.approveModalOpen = false

        this.displayToastNotification({
          type: 'success',
          icon: 'fa-check-circle',
          title: 'Approved',
          info: 'This e-zine edition has been approved.'
        })
      }, (errors) => {
        this.approving = false
      })
    },
    deleteEdition () {
      this.deleting = true
      v2Api.delete(`ezine-editions/${this.form.id}`).then((response) => {
        this.form.deleted_at = response.data.deleted_at
        this.deleting = false
        this.deleteModalOpen = false

        this.displayToastNotification({
          type: 'success',
          icon: 'fa-check-circle',
          title: 'Deactivated',
          info: 'This e-zine edition has been deactivated.'
        })
      }, (errors) => {
        this.deleting = false
      })
    }
  },
  watch: {
    selectedOffice () {
      this.back()
    }
  }
}
</script>
